import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import SearchIcon from '@mui/icons-material/Search';
// Note: Dialog/MenuItem/Select/FormControl/InputLabel kept for UpdateStatusModal
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Tooltip from '@mui/material/Tooltip';

import { BreadcrumbHeader, FilterTabs, DataTable, StatusBadge, EmptyState, ActionMenu, CreateShipmentModal } from '../../components/shared';
import { shipments, partOrders } from '../../services/dataClient';

// Break at the last space within `max` chars, or hard-cut if no space
import ContentCard from "../../components/shared/ContentCard";function truncate(text, max = 14) {
  if (!text || text.length <= max) return { short: text, truncated: false };
  const breakAt = text.lastIndexOf(' ', max);
  const short = (breakAt > 4 ? text.slice(0, breakAt) : text.slice(0, max)) + '…';
  return { short, truncated: true };
}

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
{ label: 'All', value: 'All' },
{ label: 'Active', value: 'Active' },
{ label: 'In Transit', value: 'In Transit' },
{ label: 'Delivered', value: 'Delivered' }];

function getNextStatuses(current) {
  if (current === 'Active') return ['In Transit'];
  if (current === 'In Transit') return ['Delivered'];
  return [];
}

// ─── Origin type chip ─────────────────────────────────────────────────────────
function OriginChip({ type }) {
  const isFsl = type === 'FSL';
  const isSupplier = type === 'Supplier';
  return (
    <Box
      sx={{ display: 'inline-flex',
        alignItems: 'center',
        px: '8px',
        py: '2px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 600,
        backgroundColor: isFsl ? '#E0F2F1' : isSupplier ? '#FFF3E0' : '#ECEFF1',
        color: isFsl ? '#00695C' : isSupplier ? '#E65100' : '#455A64'
      }}>
      
      {type}
    </Box>);

}

// ─── Summary bar ──────────────────────────────────────────────────────────────
function SummaryBar({ data }) {
  const total = data.length;
  const active = data.filter((s) => s.status === 'Active').length;
  const inTransit = data.filter((s) => s.status === 'In Transit').length;
  const delivered = data.filter((s) => s.status === 'Delivered').length;

  const items = [
  { label: 'Total', value: total, color: 'var(--grey-700)' },
  { label: 'Active', value: active, color: '#1565C0' },
  { label: 'In Transit', value: inTransit, color: '#4527A0' },
  { label: 'Delivered', value: delivered, color: '#2E7D32' }];

  return (
    <ContentCard
      sx={{ display: 'flex',
        gap: 0,

        overflow: 'hidden'
      }}>
      
      {items.map((item, i) =>
      <Box
        key={item.label}
        sx={{ flex: 1,
          px: 3,
          py: '12px',
          borderRight: i < items.length - 1 ? '1px solid var(--grey-200)' : 'none'
        }}>
        
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: item.color, lineHeight: 1 }}>
            {item.value}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '4px' }}>
            {item.label}
          </Typography>
        </Box>
      )}
    </ContentCard>);

}

// ─── Update Status modal ───────────────────────────────────────────────────────
function UpdateStatusModal({ open, shipment, onConfirm, onCancel }) {
  const nextStatuses = shipment ? getNextStatuses(shipment.status) : [];
  const [newStatus, setNewStatus] = useState('');

  const handleEnter = () => setNewStatus(nextStatuses[0] ?? '');

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {if (reason !== 'backdropClick') onCancel();}}
      TransitionProps={{ onEnter: handleEnter }}
      maxWidth="xs"
      fullWidth>
      
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Update Status
        <IconButton size="small" onClick={onCancel} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        <FormControl size="small" fullWidth>
          <InputLabel>New Status</InputLabel>
          <Select
            label="New Status"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}>
            
            {nextStatuses.map((s) =>
            <MenuItem key={s} value={s}>{s}</MenuItem>
            )}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onCancel}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!newStatus}
          onClick={() => onConfirm(newStatus)}
          sx={{ backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 'none',
            px: 2
          }}>
          
          Update
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function DispatchBoardPage() {
  const navigate = useNavigate();

  const [allShipments, setAllShipments] = useState(shipments.filter((s) => s.partOrderId !== null));
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'createdDate', direction: 'desc' });
  const [modalOpen, setModalOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState('');
  const [updateModal, setUpdateModal] = useState(null);

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleOpenModal = () => setModalOpen(true);

  const handleSubmit = (newShipment) => {
    setAllShipments((prev) => [newShipment, ...prev]);
    setModalOpen(false);
    setBannerMessage('Shipment created successfully.');
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
  };

  const handleUpdateStatus = (newStatus) => {
    setAllShipments((prev) =>
    prev.map((s) => s.id === updateModal.id ? { ...s, status: newStatus } : s)
    );
    setBannerMessage(`Shipment ${updateModal.shipmentNumber} updated to ${newStatus}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
    setUpdateModal(null);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allShipments
    .filter((s) => {
      if (activeTab === 'All') return true;
      return s.status === activeTab;
    })
    .filter((s) =>
    !q ||
    s.shipmentNumber.toLowerCase().includes(q) ||
    s.trackingNumber.toLowerCase().includes(q) ||
    s.customerName.toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const va = a[sortConfig.field] ?? '';
      const vb = b[sortConfig.field] ?? '';
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [allShipments, activeTab, search, sortConfig]);

  const columns = [
  {
    field: 'shipmentNumber',
    headerName: 'Shipment #',
    sortable: true,
    renderCell: (row) =>
    <Typography
      onClick={(e) => {e.stopPropagation();navigate(`/shipments/dispatch/${row.id}`);}}
      sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
      
          {row.shipmentNumber}
        </Typography>

  },
  {
    field: 'originType',
    headerName: 'Origin Type',
    sortable: true,
    renderCell: (row) => <OriginChip type={row.originType} />
  },
  {
    field: 'originLocation',
    headerName: 'Origin Location',
    sortable: true,
    renderCell: (row) => {
      const { short, truncated } = truncate(row.originLocation);
      return (
        <Tooltip title={truncated ? row.originLocation : ''} placement="top" arrow enterDelay={300} disableHoverListener={!truncated}>
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{short ?? '—'}</Typography>
          </Tooltip>);

    }
  },
  {
    field: 'resellerName',
    headerName: 'Reseller',
    sortable: true,
    renderCell: (row) => {
      const resellerName = row.partOrderId ?
      partOrders.find((o) => o.id === row.partOrderId)?.resellerName ?? null :
      null;
      const { short, truncated } = truncate(resellerName);
      return (
        <Tooltip title={truncated ? resellerName : ''} placement="top" arrow enterDelay={300} disableHoverListener={!truncated}>
            <Typography sx={{ fontSize: '0.875rem', color: resellerName ? 'var(--grey-800)' : 'var(--grey-400)' }}>
              {resellerName ? short : '—'}
            </Typography>
          </Tooltip>);

    }
  },
  {
    field: 'customerName',
    headerName: 'End-Customer',
    sortable: true,
    renderCell: (row) => {
      const { short, truncated } = truncate(row.customerName);
      return (
        <Tooltip title={truncated ? row.customerName : ''} placement="top" arrow enterDelay={300} disableHoverListener={!truncated}>
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{short}</Typography>
          </Tooltip>);

    }
  },
  { field: 'carrier', headerName: 'Carrier', sortable: true },
  { field: 'trackingNumber', headerName: 'Tracking #', sortable: false },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (row) => <StatusBadge status={row.status} />
  },
  { field: 'createdDate', headerName: 'Created Date', sortable: true },
  {
    field: 'ticketNumber',
    headerName: 'Part Order #',
    sortable: false,
    renderCell: (row) =>
    row.ticketNumber ?
    <Typography
      onClick={(e) => {e.stopPropagation();navigate(`/orders/parts/${row.partOrderId ?? ''}`);}}
      sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
      
            {row.ticketNumber}
          </Typography> :

    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>

  },
  {
    field: '_actions',
    headerName: '',
    sortable: false,
    renderCell: (row) => {
      const nextStatuses = getNextStatuses(row.status);
      const actions = [
      { label: 'View Detail', onClick: () => navigate(`/shipments/dispatch/${row.id}`) },
      ...(nextStatuses.length > 0 ?
      [{ label: 'Update Status', onClick: () => setUpdateModal(row) }] :
      []),
      ...(row.partOrderId ?
      [{ label: 'View Linked Order', onClick: () => navigate(`/orders/parts/${row.partOrderId}`) }] :
      [])];

      return (
        <Box onClick={(e) => e.stopPropagation()}>
            <ActionMenu actions={actions} hideUntilRowHover />
          </Box>);

    }
  }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Success banner */}
      {showBanner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {bannerMessage}
          </Typography>
        </Box>
      }

      <BreadcrumbHeader
        title="Dispatch Board"
        crumbs={[
        { label: 'Shipments' },
        { label: 'Dispatch Board' }]
        } />

      <SummaryBar data={allShipments} />

      <ContentCard sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FilterTabs tabs={TABS} value={activeTab} onChange={setActiveTab} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
          <TextField
            size="small"
            placeholder="Search by Shipment #, Tracking #, or Customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment:
              <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
                </InputAdornment>

            }}
            sx={{ minWidth: 400, maxWidth: 400, backgroundColor: 'var(--paper)', borderRadius: 1 }} />
          
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{ backgroundColor: '#009688',
              '&:hover': { backgroundColor: '#00796B' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none'
            }}>
            
            Create Shipment
          </Button>
        </Box>

        <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
          {filtered.length === 0 ?
          <EmptyState title="No shipments found" description="Try adjusting your filter or search query." /> :

          <DataTable
            columns={columns}
            data={filtered}
            onRowClick={(row) => navigate(`/shipments/dispatch/${row.id}`)}
            sortConfig={sortConfig}
            onSort={handleSort} />

          }
        </Box>
      </ContentCard>

      {/* Update Status modal */}
      <UpdateStatusModal
        open={Boolean(updateModal)}
        shipment={updateModal}
        onConfirm={handleUpdateStatus}
        onCancel={() => setUpdateModal(null)} />

      {/* Create Shipment modal */}
      <CreateShipmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit} />
      
    </Box>);

}